import { createContext, useContext } from 'react';

type TileContextValue = {
  /**
   * Whether something inside the tile is "active" (e.g., dropdown open).
   * When active, hover-reveal elements should stay visible.
   */
  isActive: boolean;
  /**
   * Whether the tile is being hovered.
   */
  isHovering: boolean;
  /**
   * Set the active state. Call with `true` when opening a dropdown,
   * `false` when closing. Multiple active states are ref-counted.
   */
  setActive: (active: boolean) => void;
};

export const TileHoverContext = createContext<TileContextValue | null>(null);

/**
 * Hook to access the Tile's hover state from within Tile children.
 * Returns `true` when hovering OR when something is active (like an open dropdown).
 * Returns `true` (always visible) when used outside a Tile context.
 */
export const useTileHover = (): boolean => {
  const context = useContext(TileHoverContext);
  // When outside Tile context, default to visible
  if (!context) return true;
  return context.isHovering || context.isActive;
};

/**
 * Hook to register active state within a Tile.
 * Use this in components like dropdowns that should keep hover-reveal elements visible when open.
 * Returns a no-op function when used outside a Tile context.
 */
export const useTileActive = (): ((active: boolean) => void) => {
  const context = useContext(TileHoverContext);
  return context?.setActive ?? (() => {});
};
